// ─────────────────────────────────────────────────────────────────────────────
// VENDORED from the marketing site (magic-motorsports/src/utils/makeCountries.js).
//
// The dealer-stock feed carries ~113 makes, most of which are not JDM. Both
// sites group the Make filter by country with Japan pinned first so the marques
// a buyer came for sit in the first screenful. Vendored verbatim rather than
// re-derived so the two Make dropdowns list makes in the same order.
//
// The auction page has its own hardcoded country map inside
// AuctionContent/FilterAccordion.jsx; that one groups the AUCTION feed's makes
// and is deliberately left alone. This module is for dealer stock only.
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Make -> country of origin, for grouping the dealer-stock Make filter.
 *
 * `carlistings` is a Japanese auction/dealer feed, so its make list is far
 * wider than the JDM cars we actually sell — 113 distinct makes at time of
 * writing, including Wolseley, Winnebago and Lada. Sorted A-Z, Toyota lands
 * between Suzuki and Triumph and the Japanese marques a buyer came for are
 * scattered through 90 they didn't. Grouping by country with Japan pinned to
 * the top puts the relevant marques in the first screenful.
 *
 * Grouping is by the MARQUE's country, except where the feed tags a make with
 * the market it was built for — "Nissan (USA)", "Ford (Japan)". Those are the
 * odd ones out of a JDM catalogue, so instead of padding the country blocks at
 * the top they drop to the tail in two groups: US_MARKET_COUNTRY for US-spec
 * Japanese marques, then OTHER_REGIONAL_COUNTRY for the rest.
 *
 * The feed's per-country catch-alls ("Other (UK)", "Other (Japan)") look like
 * the same pattern but aren't — they're unnamed marques from that country, so
 * they stay in their country's group.
 *
 * Anything not in the map falls into "Other", which always sorts last. New
 * makes appearing in the feed therefore degrade to a visible bucket rather
 * than vanishing from the filter.
 */

// Country of each make, keyed on the lowercased make string exactly as the
// feed stores it (already normalized to English by vehicleNames.js).
const MAKE_COUNTRIES = {
  // --- Japan ---
  'acura': 'Japan',
  'daihatsu': 'Japan',
  'eunos': 'Japan',
  'hino': 'Japan',
  'honda': 'Japan',
  'infiniti': 'Japan',
  'isuzu': 'Japan',
  'lexus': 'Japan',
  'mazda': 'Japan',
  'mitsubishi': 'Japan',
  'mitsubishi fuso': 'Japan',
  'mitsuoka': 'Japan',
  'nissan': 'Japan',
  'nissan diesel': 'Japan',
  'other (japan)': 'Japan',
  'subaru': 'Japan',
  'suzuki': 'Japan',
  'toyota': 'Japan',
  'ud trucks': 'Japan',

  // --- US-market variants of Japanese marques (bottom of the list) ---
  'honda (usa)': 'US-Market Variants',
  'lexus (usa)': 'US-Market Variants',
  'mazda (usa)': 'US-Market Variants',
  'mitsubishi (usa)': 'US-Market Variants',
  'nissan (usa)': 'US-Market Variants',
  'subaru (usa)': 'US-Market Variants',
  'suzuki (usa)': 'US-Market Variants',
  'toyota (usa)': 'US-Market Variants',

  // --- Non-US-market variants of non-Japanese marques (below the above) ---
  'ford (europe)': 'Other Regional Variants',
  // The feed writes Ford's Japanese arm both ways.
  'ford (japan)': 'Other Regional Variants',
  'japan ford': 'Other Regional Variants',

  // --- Germany ---
  'amg': 'Germany',
  'audi': 'Germany',
  'bmw': 'Germany',
  'bmw alpina': 'Germany',
  'mercedes-benz': 'Germany',
  'opel': 'Germany',
  'other (germany)': 'Germany',
  'porsche': 'Germany',
  'smart': 'Germany',
  'volkswagen': 'Germany',

  // --- United States ---
  'amc': 'United States',
  'amc jeep': 'United States',
  'buick': 'United States',
  'cadillac': 'United States',
  'chevrolet': 'United States',
  'chrysler': 'United States',
  'chrysler jeep': 'United States',
  'dodge': 'United States',
  'ford': 'United States',
  'frazer': 'United States',
  'gmc': 'United States',
  'hummer': 'United States',
  'jeep': 'United States',
  'lincoln': 'United States',
  'mercury': 'United States',
  'oldsmobile': 'United States',
  'other (usa)': 'United States',
  'plymouth': 'United States',
  'pontiac': 'United States',
  'starcraft': 'United States',
  'winnebago': 'United States',

  // --- United Kingdom ---
  'aston martin': 'United Kingdom',
  'austin': 'United Kingdom',
  'bentley': 'United Kingdom',
  // British Leyland.
  'bl': 'United Kingdom',
  'carbodies': 'United Kingdom',
  'caterham': 'United Kingdom',
  // The Coventry marque, not Daimler-Benz — 'デイムラー' in the feed is this one.
  'daimler': 'United Kingdom',
  'ginetta': 'United Kingdom',
  'healey': 'United Kingdom',
  'jaguar': 'United Kingdom',
  'land rover': 'United Kingdom',
  'lotus': 'United Kingdom',
  'marcos': 'United Kingdom',
  'mclaren': 'United Kingdom',
  'mg': 'United Kingdom',
  'mini': 'United Kingdom',
  'moke': 'United Kingdom',
  'morgan': 'United Kingdom',
  'morris': 'United Kingdom',
  'other (uk)': 'United Kingdom',
  'panther': 'United Kingdom',
  'riley': 'United Kingdom',
  'rolls-royce': 'United Kingdom',
  'rover': 'United Kingdom',
  'triumph': 'United Kingdom',
  'vanden plas': 'United Kingdom',
  'westfield': 'United Kingdom',
  'wolseley': 'United Kingdom',

  // --- Italy ---
  'abarth': 'Italy',
  'alfa romeo': 'Italy',
  'autobianchi': 'Italy',
  'de tomaso': 'Italy',
  'ferrari': 'Italy',
  'fiat': 'Italy',
  'innocenti': 'Italy',
  'lamborghini': 'Italy',
  'lancia': 'Italy',
  'maserati': 'Italy',

  // --- France ---
  'alpine': 'France',
  'bugatti': 'France',
  'citroen': 'France',
  'other (france)': 'France',
  'peugeot': 'France',
  'renault': 'France',

  // --- Sweden ---
  'saab': 'Sweden',
  'volvo': 'Sweden',

  // --- Russia ---
  'lada': 'Russia',

  // --- South Africa ---
  // Birkin builds Lotus Seven replicas in Durban, so it groups with the feed's
  // own South Africa bucket rather than with the British kit cars it copies.
  'birkin': 'South Africa',
  'other (south africa)': 'South Africa',
};

// Japan first — that's the business. The rest run largest-catalogue first.
// groupMakesByCountry appends the three tail groups after these, so neither a
// market-tagged variant nor an unmapped make can push a country down the list.
export const COUNTRY_ORDER = [
  'Japan',
  'Germany',
  'United States',
  'United Kingdom',
  'Italy',
  'France',
  'Sweden',
  'Russia',
  'South Africa',
];

// Not countries, but they occupy the same slot: a make belongs to exactly one
// group, and these three are the tail, in this order.
export const US_MARKET_COUNTRY = 'US-Market Variants';
export const OTHER_REGIONAL_COUNTRY = 'Other Regional Variants';
export const OTHER_COUNTRY = 'Other';

export const countryForMake = (make) =>
  MAKE_COUNTRIES[String(make || '').trim().toLowerCase()] || OTHER_COUNTRY;

/**
 * Group a flat make list into [{ country, makes }] in COUNTRY_ORDER, A-Z
 * within each country. Countries with no makes in the feed are dropped, so an
 * empty <optgroup> never renders.
 */
export const groupMakesByCountry = (makes = []) => {
  const buckets = new Map();

  for (const make of makes) {
    if (!make) continue;
    const country = countryForMake(make);
    if (!buckets.has(country)) buckets.set(country, []);
    buckets.get(country).push(make);
  }

  return [...COUNTRY_ORDER, US_MARKET_COUNTRY, OTHER_REGIONAL_COUNTRY, OTHER_COUNTRY]
    .filter((country) => buckets.has(country))
    .map((country) => ({
      country,
      makes: buckets.get(country).sort((a, b) => a.localeCompare(b)),
    }));
};

export default groupMakesByCountry;
